// Шелът на модалите в „Схема на заведението" — рецептата от
// .claude/skills/cloudts-design/references/modals.md, изцяло на Tailwind:
// икон чип + заглавие + подзаглавие + close чип, тяло, футър с действия.

import { Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { modalStyles, modalWrapStyle } from "../../../../utils/modalStyles";

export type ModalSize = "compact" | "default" | "form" | "wide";

// Таблицата „Размери" от modals.md: [desktop, tablet]. „form" (560) е класът
// на формите с превю/плочки от дизайна на схемата (Нова маса, Редакция на региона).
const MODAL_WIDTHS: Record<ModalSize, [number, number]> = {
  compact: [380, 420],
  default: [460, 420],
  form: [560, 520],
  wide: [640, 520],
};

export const SchemaModalShell = ({
  open,
  size = "default",
  icon,
  title,
  subtitle,
  onClose,
  footer,
  flush = false,
  children,
}: {
  open: boolean;
  size?: ModalSize;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  onClose: () => void;
  footer?: React.ReactNode;
  // Тяло без отстъпи — съдържанието си ги слага само (напр. тонирана колона
  // до ръба на модала, като в „Настани" на резервация с няколко маси).
  flush?: boolean;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useViewport();
  const [desktopWidth, tabletWidth] = MODAL_WIDTHS[size];
  const width = isMobile ? undefined : isTablet ? tabletWidth : desktopWidth;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      <div className="flex max-h-[calc(100dvh-48px)] flex-col bg-surface font-['Plus_Jakarta_Sans',system-ui,sans-serif] text-ink">
        <div className="flex shrink-0 items-start gap-3 border-b border-shade-2 px-4 py-3.5 sm:px-6 sm:py-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-brand-tint text-[20px] text-brand-deep-ink">
            {icon}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 text-[17px] font-bold leading-[1.2] tracking-[-0.3px] text-ink">
              {title}
            </h2>
            {subtitle && (
              <p className="m-0 mt-1 text-[13px] leading-[1.4] text-body">
                {subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-[14px] text-muted transition-colors duration-150 hover:bg-shade-1 hover:text-ink"
          >
            <CloseOutlined />
          </button>
        </div>

        <div className={`min-h-0 flex-1 overflow-y-auto ${flush ? "" : "p-4 sm:px-6 sm:pb-6 sm:pt-5"}`}>
          {children}
        </div>

        {footer && (
          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-shade-2 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-end sm:gap-2.5 sm:px-6">
            {footer}
          </div>
        )}
      </div>
    </Modal>
  );
};

const BUTTON_BASE =
  "flex h-11 cursor-pointer items-center justify-center gap-2 rounded-[10px] text-[14px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed sm:h-[42px]";

export const PrimaryButton = ({
  children,
  onClick,
  disabled,
  loading,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || loading}
    className={`${BUTTON_BASE} min-w-[110px] border-none bg-brand px-5 font-bold text-white hover:bg-brand-deep disabled:bg-brand/55`}
  >
    {loading && (
      <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
    )}
    {children}
  </button>
);

export const GhostButton = ({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`${BUTTON_BASE} border border-shade-3 bg-surface px-[18px] font-semibold text-ink-2 hover:bg-fill disabled:opacity-50`}
  >
    {children}
  </button>
);
